/**
 * ShopProductDialog - Product create/edit dialog for Shop administration
 * Extracted from AdminShopTab.js in Phase 4D-8
 * 
 * Features:
 * - Basic product info (title, description, category, SKU)
 * - Pricing (price, compare at price, stock)
 * - Variant builder (sizes, colors, combinations)
 * - Per-variant pricing and stock
 * - Image upload and management
 * - Product settings (inventory tracking, backorders, active, discontinued)
 * 
 * Variant Generation:
 * - Size only, Color only, or Size × Color combinations
 * - Each variant has its own price and stock
 * - Base price used as default for new variants
 */
import React, { useRef } from 'react';
import { Plus, Trash2, Layers, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

// Size and Color options for variants
const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const COLOR_OPTIONS = ['Black', 'White', 'Navy', 'Red', 'Pink', 'Purple', 'Grey', 'Green', 'Blue', 'Yellow', 'Orange'];
const CATEGORY_OPTIONS = ['Clothing', 'Accessories', 'Dance Shoes', 'Music', 'Gifts', 'Other'];

/**
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Object|null} props.editingProduct - Product being edited (null for create)
 * @param {Object} props.productForm - Form state object
 * @param {Function} props.onFormChange - Callback to update form state
 * @param {Array} props.selectedSizes - Array of selected size strings
 * @param {Function} props.onSizesChange - Callback to update selected sizes
 * @param {Array} props.selectedColors - Array of selected color strings
 * @param {Function} props.onColorsChange - Callback to update selected colors
 * @param {Object} props.variantPrices - Map of variant key to price
 * @param {Function} props.onVariantPricesChange - Callback to update variant prices
 * @param {Object} props.variantStocks - Map of variant key to stock
 * @param {Function} props.onVariantStocksChange - Callback to update variant stocks
 * @param {boolean} props.uploading - Whether image is being uploaded
 * @param {Function} props.onImageUpload - Callback when image file selected
 * @param {Function} props.onRemoveImage - Callback when image remove clicked (receives index)
 * @param {boolean} props.saving - Whether product is being saved
 * @param {Function} props.onSave - Callback when save button clicked
 * @param {string} props.apiUrl - API base URL for image paths
 * @param {React.RefObject} props.fileInputRef - Ref for hidden file input
 */
export function ShopProductDialog({
  open,
  onOpenChange,
  editingProduct,
  productForm,
  onFormChange,
  selectedSizes,
  onSizesChange,
  selectedColors,
  onColorsChange,
  variantPrices,
  onVariantPricesChange,
  variantStocks,
  onVariantStocksChange,
  uploading,
  onImageUpload,
  onRemoveImage,
  saving,
  onSave,
  apiUrl,
  fileInputRef
}) {
  /**
   * Generate variants based on selected sizes and colors
   * Matches original implementation exactly
   */
  const generateVariants = () => {
    if (selectedSizes.length === 0 && selectedColors.length === 0) {
      return [];
    }
    
    const variants = [];
    const basePrice = parseFloat(productForm.price) || 0;
    
    if (selectedSizes.length > 0 && selectedColors.length > 0) {
      // Generate Size/Color combinations
      for (const size of selectedSizes) {
        for (const color of selectedColors) {
          const key = `${size}-${color}`;
          variants.push({
            name: `${size} / ${color}`,
            options: { size, color },
            price: variantPrices[key] || basePrice,
            stock_quantity: variantStocks[key] || 0,
            track_inventory: true,
            is_active: true
          });
        }
      }
    } else if (selectedSizes.length > 0) {
      // Size only
      for (const size of selectedSizes) {
        const key = `${size}-`;
        variants.push({
          name: size,
          options: { size },
          price: variantPrices[key] || basePrice,
          stock_quantity: variantStocks[key] || 0,
          track_inventory: true,
          is_active: true
        });
      }
    } else if (selectedColors.length > 0) {
      // Color only
      for (const color of selectedColors) {
        const key = `-${color}`;
        variants.push({
          name: color,
          options: { color },
          price: variantPrices[key] || basePrice,
          stock_quantity: variantStocks[key] || 0,
          track_inventory: true,
          is_active: true
        });
      }
    }
    
    return variants;
  };

  // Toggle a size selection
  const toggleSize = (size) => {
    onSizesChange(prev => 
      prev.includes(size) 
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  // Toggle a color selection
  const toggleColor = (color) => {
    onColorsChange(prev => 
      prev.includes(color) 
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  // Update a form field
  const updateField = (field, value) => {
    onFormChange(prev => ({ ...prev, [field]: value }));
  };

  const variants = generateVariants();
  const hasVariants = selectedSizes.length > 0 || selectedColors.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </DialogTitle>
          <DialogDescription>
            {editingProduct ? 'Update product details' : 'Create a new product for your shop'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="title">Product Title *</Label>
              <Input
                id="title"
                value={productForm.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="e.g., Line Dance T-Shirt"
                data-testid="product-title"
              />
            </div>
            
            <div className="md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={productForm.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Describe your product..."
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category</Label>
              <Select 
                value={productForm.category} 
                onValueChange={(v) => updateField('category', v)}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="sku">SKU</Label>
              <Input
                id="sku"
                value={productForm.sku}
                onChange={(e) => updateField('sku', e.target.value)}
                placeholder="Optional SKU code"
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="price">Price (GBP) *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={productForm.price}
                onChange={(e) => updateField('price', e.target.value)}
                placeholder="0.00"
                data-testid="product-price"
              />
            </div>
            <div>
              <Label htmlFor="compare_price">Compare at Price</Label>
              <Input
                id="compare_price"
                type="number"
                step="0.01"
                min="0"
                value={productForm.compare_at_price}
                onChange={(e) => updateField('compare_at_price', e.target.value)}
                placeholder="Original price (optional)"
              />
            </div>
            <div>
              <Label htmlFor="stock">Stock Quantity</Label>
              <Input
                id="stock"
                type="number"
                min="0"
                value={productForm.stock_quantity}
                onChange={(e) => updateField('stock_quantity', parseInt(e.target.value) || 0)}
                disabled={hasVariants}
              />
              {hasVariants && (
                <p className="text-xs text-slate-500 mt-1">Set stock per variant below</p>
              )}
            </div>
          </div>

          {/* Variants - Size & Color */}
          <div className="space-y-4 border rounded-lg p-4 bg-slate-50">
            <h4 className="font-medium flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Product Variants
            </h4>
            
            <div>
              <Label className="mb-2 block">Sizes</Label>
              <div className="flex flex-wrap gap-2">
                {SIZE_OPTIONS.map(size => (
                  <Button
                    key={size}
                    type="button"
                    variant={selectedSizes.includes(size) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleSize(size)}
                  >
                    {size}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Colors</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map(color => (
                  <Button
                    key={color}
                    type="button"
                    variant={selectedColors.includes(color) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleColor(color)}
                  >
                    {color}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Variant Price/Stock Editor */}
            {hasVariants && (
              <div className="mt-4 space-y-2">
                <Label>Variant Stock & Pricing</Label>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {variants.map((variant, idx) => {
                    const key = `${variant.options?.size || ''}-${variant.options?.color || ''}`;
                    return (
                      <div key={idx} className="flex items-center gap-3 bg-white p-2 rounded border">
                        <span className="flex-1 text-sm font-medium">{variant.name}</span>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={variantPrices[key] ?? productForm.price}
                            onChange={(e) => onVariantPricesChange(prev => ({
                              ...prev,
                              [key]: parseFloat(e.target.value) || 0
                            }))}
                            className="w-20"
                            placeholder="Price"
                          />
                          <Input
                            type="number"
                            min="0"
                            value={variantStocks[key] ?? 0}
                            onChange={(e) => onVariantStocksChange(prev => ({
                              ...prev,
                              [key]: parseInt(e.target.value) || 0
                            }))}
                            className="w-20"
                            placeholder="Stock"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Images */}
          <div>
            <Label className="mb-2 block">Product Images</Label>
            <div className="flex flex-wrap gap-3">
              {productForm.images.map((img, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={img.startsWith('/') ? `${apiUrl}${img}` : img}
                    alt={`Product ${idx + 1}`}
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveImage(idx)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 border-2 border-dashed border-slate-300 rounded flex items-center justify-center hover:border-violet-500 transition-colors"
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                ) : (
                  <Plus className="h-5 w-5 text-slate-400" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onImageUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Settings */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <Switch
                  id="track_inventory"
                  checked={productForm.track_inventory}
                  onCheckedChange={(checked) => updateField('track_inventory', checked)}
                />
                <Label htmlFor="track_inventory">Track inventory</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="allow_backorder"
                  checked={productForm.allow_backorder}
                  onCheckedChange={(checked) => updateField('allow_backorder', checked)}
                />
                <Label htmlFor="allow_backorder" className="text-sm">
                  Allow backorders
                  <span className="block text-xs text-slate-500 font-normal">Can order when out of stock (with delay)</span>
                </Label>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <Switch
                  id="is_active"
                  checked={productForm.is_active}
                  onCheckedChange={(checked) => updateField('is_active', checked)}
                />
                <Label htmlFor="is_active">Active (visible in shop)</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="is_discontinued"
                  checked={productForm.is_discontinued}
                  onCheckedChange={(checked) => updateField('is_discontinued', checked)}
                />
                <Label htmlFor="is_discontinued" className="text-sm text-red-600">
                  Discontinued
                  <span className="block text-xs text-slate-500 font-normal">Cannot be ordered at all</span>
                </Label>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-product-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingProduct ? 'Update Product' : 'Create Product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ShopProductDialog;
